import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { getFlagEmoji } from '../lib/countries';
import { useGeographicData } from '../hooks/useGeographicData';
import SearchableSelect from './SearchableSelect';

const GeographicSelector = ({
    selectedCountry,
    selectedCity,
    onCountryChange,
    onCityChange,
    required = false,
    mode = 'destination',
    showCities = true,
    label
}) => {
    const { t } = useLanguage();
    const { countries, loading, getCitiesForCountry } = useGeographicData();
    const [availableCities, setAvailableCities] = useState([]);
    const prevCountryRef = React.useRef(selectedCountry);

    // Resolve country name to code (QR form stores names, selectors use codes)
    const resolvedCountry = React.useMemo(() => {
        if (!selectedCountry || !countries.length) return selectedCountry;
        const match = countries.find(c => c.code === selectedCountry || c.name === selectedCountry);
        return match?.code || selectedCountry;
    }, [selectedCountry, countries]);

    useEffect(() => {
        if (resolvedCountry) {
            setAvailableCities(getCitiesForCountry(resolvedCountry));
            // Only reset city when user actively changes country, not on initial load
            if (prevCountryRef.current && prevCountryRef.current !== resolvedCountry && onCityChange) {
                onCityChange('');
            }
        } else {
            setAvailableCities([]);
        }
        prevCountryRef.current = resolvedCountry;
    }, [resolvedCountry, getCitiesForCountry]);

    const defaultLabel = mode === 'origin' ? t('originCountryLabel') : t('destinationCountryLabel');
    const displayLabel = label || defaultLabel;
    const icon = mode === 'origin' ? '🚀' : '🏴';

    if (loading) {
        return <div className="text-sm text-gray-500">{t('loadingCountries')}</div>;
    }

    return (
        <div className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    {icon} {displayLabel} {required && <span className="text-red-500">*</span>}
                </label>
                <SearchableSelect
                    value={resolvedCountry || ''}
                    onChange={(val) => onCountryChange(val)}
                    required={required}
                    placeholder={t('selectCountry')}
                    searchPlaceholder={t('search') || 'Rechercher un pays…'}
                    options={countries.map(country => ({
                        value: country.code,
                        label: `${getFlagEmoji(country.code)} ${country.name}`,
                    }))}
                />

                {resolvedCountry && (
                    <div className="mt-1 text-xs text-gray-500">
                        {(() => {
                            const country = countries.find(c => c.code === resolvedCountry);
                            return country ? (
                                <span>
                                    {country.currency}{showCities && country.cities?.length > 0 && ` • ${country.cities.length} ${t('cities')}`}
                                </span>
                            ) : null;
                        })()}
                    </div>
                )}
            </div>

            {showCities && resolvedCountry && availableCities.length > 0 && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {mode === 'origin' ? t('originCityLabel') : t('destinationCityLabel')} *
                    </label>
                    <SearchableSelect
                        value={selectedCity || ''}
                        onChange={(val) => onCityChange && onCityChange(val)}
                        placeholder={t('selectCity') || 'Sélectionner une ville'}
                        searchPlaceholder={t('search') || 'Rechercher une ville…'}
                        options={availableCities.map(city => ({
                            value: city.name,
                            label: `${city.name}${city.code ? ` (${city.code})` : ''}`,
                        }))}
                    />

                </div>
            )}
        </div>
    );
};

export default GeographicSelector;
